import Axios from "axios";
import Qs from "qs";
import { decamelizeKeys } from "humps";
import { getAccessToken } from "utilities/access-token";
import { isValid, parseISO } from "date-fns";
import { getClientId } from "./client-id";
import { captureException } from "@sentry/react";

export const BASE_URL = "/api";

// Dates formats the backend might send.
export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
export const AUTO_DATE_IGNORED_KEYS = ["tag"];

const languageIsoCodesTransformations: Record<string, string> = {
    zhcn: "zh-cn",
    zhtw: "zh-tw",
};

export const transformLanguageIsoCode = (languageIsoCode: string): string => {
    const lowerCaseIsoCode = languageIsoCode.toLowerCase();
    return languageIsoCodesTransformations[lowerCaseIsoCode] ?? lowerCaseIsoCode;
};

/**
 * JSON reviver that transforms API response values: date strings to Date objects,
 * interaction types to uppercase, and language ISO codes to standard format.
 * Used by transformResponse and can be reused in tests to apply the same transforms.
 */
export const responseReviver = (key: string, value: unknown): unknown => {
    try {
        // Attempt to transform strings to dates.
        if (typeof value === "string" && DATE_PATTERN.test(value) && !AUTO_DATE_IGNORED_KEYS.includes(key)) {
            try {
                const date = parseISO(value);
                return isValid(date)
                    ? parseISO(
                          value.replace(
                              /([+-])(\d{2}:\d{2})/,
                              (_, sign, offset) => `${sign === "+" ? "-" : "+"}${offset}`
                          )
                      )
                    : value;
            } catch {
                return value;
            }
        }

        if ((key === "interactionType" || key === "topInteractionType") && typeof value === "string") {
            return value.toUpperCase().replaceAll("_", " ");
        }

        if ((key === "interactionType" || key === "interactionTypes") && Array.isArray(value)) {
            return value.map((interaction: string) => interaction.toUpperCase().replaceAll("_", " "));
        }

        if (key === "language" && typeof value === "string") {
            return languageIsoCodesTransformations[value] ?? value;
        }
        if (key === "languageIsoCode" && typeof value === "string") {
            return transformLanguageIsoCode(value);
        }

        if ((key === "languageIsoCode" || key === "languageIsoCodes") && Array.isArray(value)) {
            return value.map((languageIsoCode: string) => transformLanguageIsoCode(languageIsoCode));
        }

        return value;
    } catch (error) {
        captureException(error);
        return value;
    }
};

const axios = Axios.create({
    baseURL: BASE_URL,
    paramsSerializer: (parameters) =>
        Qs.stringify(decamelizeKeys(parameters), {
            arrayFormat: "repeat",
            skipNulls: true,
        }),
    transformResponse: (data) => {
        try {
            return JSON.parse(data, responseReviver);
        } catch (error) {
            captureException(error);
            // If there was an error while parsing the JSON, return JSON string.
            return data;
        }
    },
});

/**
 * Put the access token in a header.
 */
export const createAuthorisationHeader = () => `Bearer ${getAccessToken()}`;

axios.interceptors.request.use((request) => {
    const clientId = getClientId();

    if (clientId !== null) {
        request.headers.set("WatchTower-Client-ID", clientId);
    }
    request.headers.set("Authorization", createAuthorisationHeader());
    return request;
});

export { axios };

export default Axios;
